'use client';

import { useId, useState } from 'react';
import { RichText } from '@payloadcms/richtext-lexical/react';
import { normalizeLexical } from '@/lib/normalizeLexical';

function Answer({ data }: { data: any }) {
  if (!data) return null;
  // Every answer is server-rendered now (collapsed ones are `hidden`, not
  // absent), so a malformed legacy Lexical tree can no longer hide behind a
  // click: repair it here (missing/invalid indent → #117, nested paragraphs →
  // invalid <p><p> → hydration #418). The blog-post route renders this block
  // WITHOUT the normalizeLexicalDeep + BlockErrorBoundary that [...slug] applies.
  // Pure + deterministic, so server and client output stay identical.
  if (typeof data === 'object') return <RichText data={normalizeLexical(data)} />;
  return <p>{data}</p>;
}

export function FaqBlockRenderer({ block }: { block: any }) {
  const variant = block.variant || 'accordion';
  const items = block.items || [];
  const isAlt = ((block._sectionIndex ?? 0) % 2) === 1;

  return (
    <section className="py-16 lg:py-24" style={{ backgroundColor: isAlt ? 'var(--color-t-section-alt)' : 'var(--color-t-section-light)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {block.heading && (
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-center mb-12" style={{ color: 'var(--color-t-heading)' }}>
            {block.heading}
          </h2>
        )}
        {variant === 'grid' ? <FaqGrid items={items} /> : <FaqAccordion items={items} expandAll={!!block.expandAllByDefault} />}
      </div>
    </section>
  );
}

function FaqAccordion({ items, expandAll = false }: { items: any[]; expandAll?: boolean }) {
  // SSR-safe id prefix: identical on server and client, unique per accordion,
  // so several FAQ blocks on one page never collide.
  const baseId = useId();
  // Set of open indices so multiple answers can be open at once. When
  // expandAll is set, every item starts open; visitors can still collapse any.
  const [openSet, setOpenSet] = useState<Set<number>>(() =>
    expandAll ? new Set(items.map((_: any, i: number) => i)) : new Set<number>(),
  );
  const toggle = (i: number) =>
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  return (
    <div className="max-w-2xl mx-auto space-y-2">
      {items.map((item: any, i: number) => {
        const isOpen = openSet.has(i);
        const buttonId = `${baseId}-q-${i}`;
        const panelId = `${baseId}-a-${i}`;
        return (
          <div
            key={i}
            className="rounded-xl border overflow-hidden"
            style={{ borderColor: 'color-mix(in srgb, var(--color-t-primary) 10%, transparent)' }}
          >
            <button
              type="button"
              id={buttonId}
              aria-expanded={isOpen}
              aria-controls={panelId}
              className="w-full text-left px-6 py-4 flex justify-between items-center gap-4 transition-colors cursor-pointer"
              style={{ backgroundColor: 'transparent' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--color-t-primary) 4%, transparent)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              onClick={() => toggle(i)}
            >
              <span className="text-sm font-semibold" style={{ color: 'var(--color-t-heading)' }}>{item.question}</span>
              <svg
                aria-hidden="true"
                focusable="false"
                className={`w-4 h-4 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
                fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
                style={{ color: 'var(--color-t-body)' }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
            {/* Always rendered — collapsed answers are `hidden`, not absent — so
                the server HTML carries every answer the FAQPage schema claims
                and crawlers index the full text. (`hidden` still keeps a
                collapsed answer out of the accessibility tree and out of the
                browser's find-in-page until the visitor opens it.) */}
            <div
              id={panelId}
              role="region"
              aria-labelledby={buttonId}
              hidden={!isOpen}
              className="px-6 pb-4 text-sm leading-relaxed"
              style={{ color: 'var(--color-t-body)' }}
            >
              <Answer data={item.answer} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FaqGrid({ items }: { items: any[] }) {
  return (
    <div className="grid sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
      {items.map((item: any, i: number) => (
        <div
          key={i}
          className="rounded-xl border p-6"
          style={{ borderColor: 'color-mix(in srgb, var(--color-t-primary) 10%, transparent)' }}
        >
          <h3 className="text-sm font-bold mb-2" style={{ color: 'var(--color-t-heading)' }}>{item.question}</h3>
          <div className="text-sm leading-relaxed" style={{ color: 'var(--color-t-body)' }}><Answer data={item.answer} /></div>
        </div>
      ))}
    </div>
  );
}
